/**
 * FleetManager V3 - Profile Context
 * Provides profile state and methods throughout the app
 *
 * V3: 5 profiles, tab_overrides (none/view/full), read_only mode, quick_actions
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { uiProfileAPI } from '../../api';
import i18n from '../../i18n/index.js';
import { useAuth } from './AuthContext.jsx';

// Fallback profiles if API fails (V3).
// Built by a FUNCTION, not a module-level constant: `name`/`description` are user-visible and
// must follow the current language (piège #1 — une constante de module est figée au chargement,
// avant tout changement de langue). Codes, colors, modules, widgets stay literal (technical).
function buildFallbackProfiles() {
  return {
    conducteur: {
      id: 'conducteur',
      code: 'conducteur',
      name: i18n.t('uiProfiles.context.fallback.conducteur.name'),
      description: i18n.t('uiProfiles.context.fallback.conducteur.description'),
      color: '#f59e0b',
      icon: 'UserCircle',
      // Durci le 2026-09-13 (voir seed_ui_profiles.mjs) : liste blanche stricte,
      // rien du parc — `pool_driver` est l'espace réel du conducteur.
      modules: ['pool_driver'],
      widgets: ['pool_status', 'quick_actions'],
      tab_overrides: {},
      read_only: false,
      quick_actions: ['goto_pool'],
      version: 3,
      nav_style: 'minimal',
      max_menu_depth: 1
    },
    referent_flotte: {
      id: 'referent_flotte',
      code: 'referent_flotte',
      name: i18n.t('uiProfiles.context.fallback.referent_flotte.name'),
      description: i18n.t('uiProfiles.context.fallback.referent_flotte.description'),
      color: '#10b981',
      icon: 'Table2',
      modules: ['dashboard', 'vehicles', 'drivers', 'teams', 'sites', 'pipeline', 'maintenance', 'inspections', 'fuel', 'fines', 'documents', 'insurance', 'costs', 'suppliers', 'alerts', 'reports', 'claims', 'events', 'tools'],
      widgets: ['alerts_priority', 'vehicles_status', 'drivers_availability', 'sites_overview', 'upcoming_deadlines', 'costs_month', 'claims_open', 'resource_alerts', 'quick_actions'],
      tab_overrides: {
        suppliers: { directory: 'full', contracts: 'none', performance: 'none' },
        reports: { conformite: 'full', couts: 'view', risques: 'view', synthese_hebdo: 'none', synthese: 'none' }
      },
      read_only: false,
      quick_actions: ['add_vehicle', 'add_driver', 'add_fuel', 'add_maintenance', 'declare_claim', 'add_document', 'view_sites'],
      version: 3,
      nav_style: 'grouped',
      max_menu_depth: 2
    },
    gestionnaire_flotte: {
      id: 'gestionnaire_flotte',
      code: 'gestionnaire_flotte',
      name: i18n.t('uiProfiles.context.fallback.gestionnaire_flotte.name'),
      description: i18n.t('uiProfiles.context.fallback.gestionnaire_flotte.description'),
      color: '#3b82f6',
      icon: 'UserCog',
      modules: ['dashboard', 'vehicles', 'drivers', 'teams', 'sites', 'pipeline', 'maintenance', 'inspections', 'fuel', 'fines', 'documents', 'insurance', 'claims', 'costs', 'suppliers', 'alerts', 'reports', 'events', 'tools'],
      widgets: ['alerts_priority', 'vehicles_status', 'drivers_availability', 'sites_overview', 'upcoming_deadlines', 'costs_month', 'claims_open', 'resource_alerts', 'quick_actions'],
      tab_overrides: {},
      read_only: false,
      quick_actions: ['add_vehicle', 'add_driver', 'add_fuel', 'add_maintenance', 'declare_claim', 'add_document', 'view_sites', 'run_report'],
      version: 3,
      nav_style: 'grouped',
      max_menu_depth: 2
    },
    directeur_flotte: {
      id: 'directeur_flotte',
      code: 'directeur_flotte',
      name: i18n.t('uiProfiles.context.fallback.directeur_flotte.name'),
      description: i18n.t('uiProfiles.context.fallback.directeur_flotte.description'),
      color: '#8b5cf6',
      icon: 'BarChart3',
      modules: ['dashboard', 'vehicles', 'drivers', 'teams', 'sites', 'pipeline', 'maintenance', 'inspections', 'fuel', 'fines', 'documents', 'insurance', 'claims', 'costs', 'suppliers', 'telematics', 'tools', 'compliance', 'reports', 'report-archive', 'alerts', 'shares'],
      widgets: ['kpi_overview', 'alerts_priority', 'vehicles_status', 'drivers_availability', 'sites_overview', 'costs_month', 'claims_open', 'upcoming_deadlines', 'recent_activity', 'resource_alerts', 'quick_actions'],
      tab_overrides: {},
      read_only: false,
      quick_actions: ['add_vehicle', 'add_driver', 'add_fuel', 'add_maintenance', 'declare_claim', 'add_document', 'view_sites', 'run_report'],
      version: 3,
      nav_style: 'full',
      max_menu_depth: 3
    },
    observateur: {
      id: 'observateur',
      code: 'observateur',
      name: i18n.t('uiProfiles.context.fallback.observateur.name'),
      description: i18n.t('uiProfiles.context.fallback.observateur.description'),
      color: '#6b7280',
      icon: 'Eye',
      modules: ['dashboard', 'vehicles', 'drivers', 'sites', 'maintenance', 'fuel', 'fines', 'documents', 'insurance', 'claims', 'costs', 'reports'],
      widgets: ['alerts_priority', 'vehicles_status', 'drivers_availability', 'sites_overview', 'upcoming_deadlines', 'costs_month', 'claims_open', 'resource_alerts'],
      tab_overrides: {},
      read_only: true,
      quick_actions: [],
      version: 3,
      nav_style: 'grouped',
      max_menu_depth: 2
    }
  };
}

const DEFAULT_PROFILE_CODE = 'referent_flotte';

// Role → default profile mapping (V3)
const ROLE_PROFILE_MAP = {
  admin: 'gestionnaire_flotte',
  manager: 'gestionnaire_flotte',
  fleet_manager: 'directeur_flotte',
  org_admin: 'directeur_flotte',
  viewer: 'observateur',
  driver: 'conducteur',
  mechanic: 'conducteur',
};

function getDefaultProfileForRole(role) {
  return ROLE_PROFILE_MAP[role] || DEFAULT_PROFILE_CODE;
}

/**
 * Parse a V3 profile from API data, handling JSON string fields
 */
function parseProfile(p) {
  const parsed = {
    ...p,
    id: p.code,
    modules: typeof p.modules === 'string' ? JSON.parse(p.modules) : (p.modules || []),
    widgets: typeof p.widgets === 'string' ? JSON.parse(p.widgets) : (p.widgets || []),
    tab_overrides: parseJsonField(p.tab_overrides, {}),
    quick_actions: parseJsonField(p.quick_actions, []),
    read_only: p.read_only === 1 || p.read_only === true,
    version: p.version || 2,
  };
  return parsed;
}

function parseJsonField(value, fallback) {
  if (!value) return fallback;
  if (typeof value === 'object') return value;
  try { return JSON.parse(value); } catch { return fallback; }
}

const ProfileContext = createContext(null);

export function ProfileProvider({ children }) {
  const [profiles, setProfiles] = useState(buildFallbackProfiles);
  const [profileId, setProfileId] = useState(DEFAULT_PROFILE_CODE);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const { isAuthenticated, isLoading: authLoading, user: authUser } = useAuth();

  useEffect(() => {
    if (authLoading) return;

    if (!isAuthenticated) {
      applyFallback();
      setIsLoading(false);
      return;
    }

    async function loadProfiles() {
      setIsLoading(true);
      setError(null);

      try {
        const response = await uiProfileAPI.getActive();

        if (response.ok && response.data.length > 0) {
          const profilesMap = {};
          response.data.forEach(p => {
            profilesMap[p.code] = parseProfile(p);
          });

          setProfiles(profilesMap);

          // Determine which profile to use
          const userProfileId = authUser?.ui_profile_id;
          const dbAssigned = userProfileId
            ? response.data.find(p => p.id === userProfileId)
            : null;

          if (dbAssigned && profilesMap[dbAssigned.code]) {
            setProfileId(dbAssigned.code);
            localStorage.setItem('fm_profile', dbAssigned.code);
          } else {
            const roleDefault = authUser?.role ? getDefaultProfileForRole(authUser.role) : null;
            if (roleDefault && profilesMap[roleDefault]) {
              setProfileId(roleDefault);
              localStorage.setItem('fm_profile', roleDefault);
            } else {
              const saved = localStorage.getItem('fm_profile');
              if (saved && profilesMap[saved]) {
                setProfileId(saved);
              } else {
                const defaultProfile = response.data.find(p => p.is_default);
                if (defaultProfile) {
                  setProfileId(defaultProfile.code);
                  localStorage.setItem('fm_profile', defaultProfile.code);
                } else {
                  const firstProfile = response.data[0];
                  if (firstProfile) {
                    setProfileId(firstProfile.code);
                    localStorage.setItem('fm_profile', firstProfile.code);
                  }
                }
              }
            }
          }
        } else {
          console.warn('No profiles from API, using fallback');
          applyFallback();
        }
      } catch (err) {
        console.error('Failed to load profiles from API:', err);
        setError(err.message);
        applyFallback();
      } finally {
        setIsLoading(false);
      }
    }

    loadProfiles();

    function applyFallback() {
      const fallbackProfiles = buildFallbackProfiles();
      setProfiles(fallbackProfiles);
      // H1 fix (2026-09-13): this path also runs when the /ui_profiles call fails
      // for an ALREADY authenticated user (see catch below) — not just at logout.
      // A stale 'fm_profile' from a previous session (e.g. an admin's, in the same
      // tab) must never outrank the CURRENT authUser's role: a driver whose
      // profiles fetch fails must not silently inherit the last admin profile.
      const roleDefault = authUser?.role ? getDefaultProfileForRole(authUser.role) : null;
      if (roleDefault && fallbackProfiles[roleDefault]) {
        setProfileId(roleDefault);
        localStorage.setItem('fm_profile', roleDefault);
        return;
      }
      const saved = localStorage.getItem('fm_profile');
      if (saved && fallbackProfiles[saved]) {
        setProfileId(saved);
      } else {
        setProfileId(DEFAULT_PROFILE_CODE);
        localStorage.setItem('fm_profile', DEFAULT_PROFILE_CODE);
      }
    }
  }, [isAuthenticated, authLoading, authUser]);

  // Get current profile object
  const profile = profiles[profileId] || profiles[DEFAULT_PROFILE_CODE] || Object.values(profiles)[0];

  // Switch to a different profile
  const switchProfile = useCallback((newProfileId) => {
    if (profiles[newProfileId]) {
      setProfileId(newProfileId);
      localStorage.setItem('fm_profile', newProfileId);
    }
  }, [profiles]);

  // Refresh profiles from API
  const refreshProfiles = useCallback(async () => {
    try {
      const response = await uiProfileAPI.getActive();
      if (response.ok && response.data.length > 0) {
        const profilesMap = {};
        response.data.forEach(p => {
          profilesMap[p.code] = parseProfile(p);
        });
        setProfiles(profilesMap);
      }
    } catch (err) {
      console.error('Failed to refresh profiles:', err);
    }
  }, []);

  // Check if a module is visible for current profile
  const isModuleVisible = useCallback((moduleId) => {
    return profile?.modules?.includes(moduleId) ?? false;
  }, [profile]);

  // Check if a widget is enabled for current profile
  const isWidgetEnabled = useCallback((widgetId) => {
    return profile?.widgets?.includes(widgetId) ?? false;
  }, [profile]);

  // V3: Is the current profile read-only?
  const isReadOnly = profile?.read_only === true;

  // V3: Get tab permission for a module tab
  // Returns 'full' | 'view' | 'none'
  // If module is in profile.modules but tab is NOT in tab_overrides → 'full' (backward-compatible)
  const getTabPermission = useCallback((moduleId, tabId) => {
    if (!profile) return 'full';
    // If the module is not in profile modules at all, no access
    if (!profile.modules?.includes(moduleId)) return 'none';
    // If read_only, max permission is 'view'
    const overrides = profile.tab_overrides || {};
    const moduleOverrides = overrides[moduleId];
    if (!moduleOverrides) {
      return profile.read_only ? 'view' : 'full';
    }
    const perm = moduleOverrides[tabId];
    if (!perm) {
      return profile.read_only ? 'view' : 'full';
    }
    // If read_only, cap at 'view'
    if (profile.read_only && perm === 'full') return 'view';
    return perm;
  }, [profile]);

  // V3: Is a tab visible? (permission !== 'none')
  const isTabVisible = useCallback((moduleId, tabId) => {
    return getTabPermission(moduleId, tabId) !== 'none';
  }, [getTabPermission]);

  // V3: Filter an array of tabs for a module
  const filterTabs = useCallback((moduleId, tabsArray) => {
    return tabsArray.filter(tab => {
      const tabId = typeof tab === 'string' ? tab : tab.id;
      return isTabVisible(moduleId, tabId);
    });
  }, [isTabVisible]);

  // V3: Can write? (full permission AND not read_only)
  const canWrite = useCallback((moduleId, tabId) => {
    if (profile?.read_only) return false;
    if (!moduleId) return !profile?.read_only;
    if (!tabId) {
      // Module-level: can write if module is visible and not read_only
      return profile?.modules?.includes(moduleId) ?? false;
    }
    return getTabPermission(moduleId, tabId) === 'full';
  }, [profile, getTabPermission]);

  const value = React.useMemo(() => ({
    // State
    profileId,
    profile,
    profiles,
    isLoading,
    error,

    // Actions
    switchProfile,
    refreshProfiles,
    isModuleVisible,
    isWidgetEnabled,

    // V3: Permission methods
    isReadOnly,
    getTabPermission,
    isTabVisible,
    filterTabs,
    canWrite,

    // Module gating (Phase 11 Diamond): alias for isModuleVisible
    hasModule: isModuleVisible,

    // Helper to get profile by code
    getProfile: (code) => profiles[code],
  }), [
    profileId,
    profile,
    profiles,
    isLoading,
    error,
    switchProfile,
    refreshProfiles,
    isModuleVisible,
    isWidgetEnabled,
    isReadOnly,
    getTabPermission,
    isTabVisible,
    filterTabs,
    canWrite,
  ]);

  return (
    <ProfileContext.Provider value={value}>
      {children}
    </ProfileContext.Provider>
  );
}

export function useProfile() {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error('useProfile must be used within a ProfileProvider');
  }
  return context;
}

export default ProfileContext;
